import domtoimage from 'dom-to-image';
import jsPDF, { jsPDFOptions } from 'jspdf';
import { RefObject } from 'react';

type ExportToPDFFunction = (
  contentRef: RefObject<HTMLDivElement>,
  filename: string,
  pdfProps: jsPDFOptions & { imgWidth?: number },
) => Promise<void>;

type CardElement = {
  element: HTMLElement;
  height: number;
  offsetTop: number;
};

const useExportToPDF = (): ExportToPDFFunction => {
  const measureCardElements = (content: HTMLElement): CardElement[] => {
    const cardElements = content.querySelectorAll(
      '[data-card="session-card"], [class*="rounded-lg"], .card, [class*="card"], [class*="bg-neutral-white"], [class*="border-1"], [class*="border-neutral-800"]',
    );
    const cards: CardElement[] = [];

    cardElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      const rect = htmlElement.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();

      const cardData = {
        element: htmlElement,
        height: rect.height,
        offsetTop: rect.top - contentRect.top,
      };

      cards.push(cardData);
    });

    if (cards.length === 0) {
      const allChildren = content.children;
      for (let i = 0; i < allChildren.length; i++) {
        const child = allChildren[i] as HTMLElement;
        const rect = child.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();

        if (rect.height > 50) {
          cards.push({
            element: child,
            height: rect.height,
            offsetTop: rect.top - contentRect.top,
          });
        }
      }
    }
    return cards;
  };

  const calculateOptimalPageBreaks = (
    cards: CardElement[],
    pageHeight: number,
    imgScale: number,
  ): number[] => {
    const pageBreaks: number[] = [0];
    let currentPageStartY = 0;
    const safetyMargin = 10;

    cards.forEach((card, index) => {
      const cardTopInPDF = card.offsetTop * imgScale;
      const cardHeightInPDF = card.height * imgScale;
      const positionOnCurrentPage = cardTopInPDF - currentPageStartY;
      const cardEndOnCurrentPage = positionOnCurrentPage + cardHeightInPDF;
      const availableSpace = pageHeight - safetyMargin;

      const isFirstPage = pageBreaks.length === 1;
      const minContentHeightForFirstPage = pageHeight * 0.4;

      if (cardEndOnCurrentPage > availableSpace && positionOnCurrentPage > 0) {
        if (isFirstPage && positionOnCurrentPage < minContentHeightForFirstPage) {
          // Keep on first page
        } else {
          pageBreaks.push(cardTopInPDF);
          currentPageStartY = cardTopInPDF;
        }
      }
    });

    return pageBreaks;
  };

  const exportToPDF: ExportToPDFFunction = async (contentRef, filename, pdfProps) => {
    if (!contentRef.current) {
      console.error('Content reference is not available.');
      return;
    }

    const content = contentRef.current;

    try {
      const cards = measureCardElements(content);

      const dataUrl = await domtoimage.toPng(content, {
        width: content.scrollWidth,
        height: content.scrollHeight,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: content.scrollWidth + 'px',
          height: content.scrollHeight + 'px',
        },
        quality: 0.95,
        bgcolor: '#ffffff',
      });

      const img = new Image();
      img.src = dataUrl;

      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const pdf = new jsPDF(pdfProps);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfProps.imgWidth || pdfWidth;
      const imgHeight = (img.height * imgWidth) / img.width;
      const imgScale = imgWidth / img.width;
      const topMargin = 8;

      if (imgHeight <= pdfHeight - topMargin) {
        pdf.addImage(dataUrl, 'PNG', 0, topMargin, imgWidth, imgHeight);
      } else {
        const pageBreaks =
          cards.length > 0
            ? calculateOptimalPageBreaks(cards, pdfHeight - topMargin, imgScale)
            : [];

        if (pageBreaks.length <= 1) {
          let remainingHeight = imgHeight;
          let sourceY = 0;
          let pageNumber = 0;

          while (remainingHeight > 0) {
            if (pageNumber > 0) {
              pdf.addPage();
            }
            const currentPageHeight = Math.min(pdfHeight - topMargin, remainingHeight);
            const cropCanvas = document.createElement('canvas');
            const cropCtx = cropCanvas.getContext('2d');

            if (cropCtx) {
              cropCanvas.width = img.width;
              cropCanvas.height = (currentPageHeight * img.width) / imgWidth;

              cropCtx.drawImage(
                img,
                0,
                (sourceY * img.width) / imgWidth,
                img.width,
                cropCanvas.height,
                0,
                0,
                img.width,
                cropCanvas.height,
              );

              const croppedDataUrl = cropCanvas.toDataURL('image/png');
              pdf.addImage(croppedDataUrl, 'PNG', 0, topMargin, imgWidth, currentPageHeight);
            }

            sourceY += currentPageHeight;
            remainingHeight -= currentPageHeight;
            pageNumber++;
          }
        } else {
          for (let i = 0; i < pageBreaks.length; i++) {
            if (i > 0) {
              pdf.addPage();
            }

            const startY = pageBreaks[i];
            const nextBreak = pageBreaks[i + 1];
            const endY = nextBreak !== undefined ? nextBreak : imgHeight;
            const sectionHeight = endY - startY;

            const cropCanvas = document.createElement('canvas');
            const cropCtx = cropCanvas.getContext('2d');

            if (cropCtx && sectionHeight > 0) {
              cropCanvas.width = img.width;
              cropCanvas.height = (sectionHeight * img.width) / imgWidth;

              cropCtx.drawImage(
                img,
                0,
                (startY * img.width) / imgWidth,
                img.width,
                cropCanvas.height,
                0,
                0,
                img.width,
                cropCanvas.height,
              );

              const croppedDataUrl = cropCanvas.toDataURL('image/png');
              pdf.addImage(croppedDataUrl, 'PNG', 0, topMargin, imgWidth, sectionHeight);
            }
          }
        }
      }

      console.log('PDF wygenerowany pomyślnie');
      pdf.save(filename);
    } catch (error) {
      console.error('Błąd podczas eksportu:', error);
    }
  };

  return exportToPDF;
};

export default useExportToPDF;
